import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { changeFilter, resetCategoryFilter, updateCategoryByFilters } from '~/Actions/ActionApp';
import { State } from '~/Reducers';
import { playButtonClickSound } from '~/api/WoWsClient';
import dwhExport from '~/api/dwhExport';
import { DWH_EVENTS, FILTER_CURRENCY_NAME } from '~/const';
import { scrollTop } from '~/utils/scrollTop';
import { flat, isEmptyObject } from '~/utils/utils';
import ActiveFilters, { ACTIVE_FILTERS_TYPE } from '~/components/Filters/ActiveFilter';

interface IActiveCategoryFilters {
    category: ICategoryList;
    className?: string;
}

interface IStateSelectorActiveFilters {
    filters: IFilters;
    disabledCategories: string[];
    diapason: IFiltersDiapasonCurrencies;
    facetCurrenciesState: IFacetCurrenciesState;
    pricesInfo: IBundlePricesInfo;
}

const stateSelectorActiveFilters = (state: State): IStateSelectorActiveFilters => {
    return {
        filters: state.ReducerApp.filters,
        disabledCategories: state.ReducerApp.disabledCategories,
        diapason: state.ReducerApp.filtersDiapasonCurrency,
        pricesInfo: state.ReducerApp.categoryBundlesPricesInfo,
        facetCurrenciesState: state.ReducerApp.facetCurrenciesState,
    };
};

const ActiveCategoryFilters = ({ category, className }: IActiveCategoryFilters) => {
    const dispatch = useDispatch();
    const { disabledCategories, filters, diapason, facetCurrenciesState, pricesInfo } = useSelector<State, IStateSelectorActiveFilters>(stateSelectorActiveFilters, shallowEqual);
    const isDisabledCategory = disabledCategories.includes(category);
    const activeFilters = React.useMemo<ACTIVE_FILTERS_TYPE>(() => {
        if (!filters?.[category] || !flat(Object.values(filters?.[category])).length) {
            return {};
        }
        return Object.keys(filters?.[category]).reduce((accumulator: ACTIVE_FILTERS_TYPE, filterKey: FILTER_INFO_NAME) => {
            if (!accumulator[filterKey]) {
                accumulator[filterKey] = { values: [], diapasons: {} };
            }
            filters[category][filterKey].forEach((value: string) => {
                if (filterKey === FILTER_CURRENCY_NAME) {
                    accumulator[filterKey].values.push(value);
                    const currencyDiapason = diapason?.[category]?.[value];
                    const defaultValues = pricesInfo?.[category]?.[value];
                    const facetCurrenciesDiapason = facetCurrenciesState?.[category]?.[value] as Diapason_Type;
                    let { min, max } = facetCurrenciesDiapason || defaultValues;

                    if (currencyDiapason?.min && currencyDiapason?.max) {
                        [min, max] = [currencyDiapason.min, currencyDiapason.max];
                    }

                    accumulator[filterKey].diapasons[value] = { max, min };
                } else {
                    accumulator[filterKey].values.push(value);
                }
            });
            return accumulator;
        }, {});
    }, [filters?.[category], diapason, facetCurrenciesState]);

    if (isEmptyObject(activeFilters) || isDisabledCategory) {
        return null;
    }

    const resetAll = (event: React.MouseEvent) => {
        event.stopPropagation();
        playButtonClickSound();
        dispatch(resetCategoryFilter(category));
        dwhExport.send(DWH_EVENTS.FILTERS_RESET, { category: category });
        scrollTop(0, true);
    };

    const resetFilter = (filterName: FILTER_INFO_NAME, value: any) => {
        scrollTop(0, true);
        dispatch(changeFilter(category, filterName, value));
        dispatch(updateCategoryByFilters(category, filterName, value));
    };

    return <ActiveFilters className={className} activeFilters={activeFilters} resetAll={resetAll} onClick={resetFilter} />;
};

export default ActiveCategoryFilters;
